var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useContext, useRef, } from "react";
import { emptyFilters, FilterContext, getFilters, setFilters, updateFilters, } from "../../providers/FilterProvider";
import clsx from "clsx";
import styles from "./FilterSelectPanel.module.scss";
import { AutoCompleteFilter, HidingOptionlessComponent, } from "./AutoCompleteFilter";
import { TestTag, getAutomationTag } from "../../common/testAutomation";
import { useGetLookupsFiltersQuery, useGetDataFiltersQuery, } from "../../store/api/core";
import { Carets } from "../PaneSelector/PaneSelector";
import { useClient } from "../../hooks/client";
import { CautionCard, useCountInfo } from "../CautionCard/CautionCard";
import ChevronLeft from "../../icons/ChevronLeft";
import Button from "../Button";
import { ButtonVariant } from "../Button/Button";
import ResetButton from "../ResetButton/ResetButton";
import { AvailableFilters, dataFilters, getEntity } from "../../common/filters";
import { useReloadableReportData } from "../../providers/ReloadableReportDataProvider";
import { isCohort, useDynamicOptions } from "./Filters";
var FilterCount = function (_a) {
    var selectors = _a.selectors;
    var getFilters = useContext(FilterContext).getFilters;
    return Object.entries(getFilters()).reduce(function (sum, _a) {
        var key = _a[0], value = _a[1];
        return (selectors.has(key) ? sum + value.length : sum);
    }, 0);
};
var FilterDropdown = function (_a) {
    var title = _a.title, children = _a.children, selectors = _a.selectors, expanded = _a.expanded;
    var _b = useState(expanded), isOpen = _b[0], setIsOpen = _b[1];
    return (_jsxs("div", __assign({ className: styles.dropdownContainer }, getAutomationTag(TestTag.FilterSection, title.toLowerCase().replace(/\s+/g, "-")), { children: [_jsx("div", __assign({ className: styles.dropdownHeader }, getAutomationTag(TestTag.FilterDropdownHeader, title.toLowerCase().replace(/\s+/g, "-")), { onClick: function () { return setIsOpen(!isOpen); } }, { children: _jsxs("div", __assign({ className: styles.dropdownTitle }, { children: [title, " ", _jsxs("span", __assign({ className: styles.count }, { children: [" ", _jsx(FilterCount, { selectors: selectors }), " "] })), _jsx("span", __assign({ className: styles.carets }, { children: _jsx(Carets, { isUp: isOpen, sx: {
                                    background: "var(--background-color)",
                                    height: "32px",
                                    width: "32px",
                                } }) }))] })) })), _jsx("div", __assign({ className: isOpen ? styles.children : styles.hidden }, { children: children }))] })));
};
var primaryFilters = [
    {
        filter: AvailableFilters.Project,
    },
    {
        filter: AvailableFilters.JobLevels,
        description: "We recommend filtering to a single leadership level, " +
            "as expectations and scores naturally differ. Results may be " +
            "misleading unless used for specific purposes, such as identifying " +
            "enterprise-wide strengths or gaps.",
    },
    {
        filter: AvailableFilters.BusinessFunctions,
    },
    {
        filter: AvailableFilters.BusinessUnits,
    },
    {
        filter: AvailableFilters.AssessmentJourney,
        description: "We recommend filtering to a single assessment journey, " +
            "as context differences can meaningfully affect scores and are often " +
            "misinterpreted when combined.",
    },
];
var primarySelectors = new Set(primaryFilters.map(function (_a) {
    var filter = _a.filter;
    return dataFilters[filter].entity;
}));
var useLookupOptions = function (key) {
    var client = useClient();
    var lookups = useGetLookupsFiltersQuery({ clientId: client.clientId || "" }, { shouldSkipCall: function () { return !client.clientId; } });
    var options = useFilterOptions(key)();
    return function () {
        var _a, _b;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var lookupCodeMap = ((_b = (_a = lookups === null || lookups === void 0 ? void 0 : lookups.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.lookupCodeMap) || {};
        if (!lookupCodeMap) {
            return options;
        }
        return options.map(function (_a) {
            var label = _a.label, value = _a.value;
            return ({
                value: value,
                label: lookupCodeMap[value] || label,
            });
        });
    };
};
var useFilterOptions = function (key) { return function () {
    var _a, _b;
    var client = useClient();
    var getFilters = useContext(FilterContext).getFilters;
    var filters = getFilters();
    var filterOptionsData = useGetDataFiltersQuery({
        clientId: client.clientId || "",
        filters: filters,
    }, { shouldSkipCall: function () { return !client.clientId; } });
    var options = ((_b = (_a = filterOptionsData === null || filterOptionsData === void 0 ? void 0 : filterOptionsData.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b[key]) || [];
    return options;
}; };
var optionGetters = (_a = {},
    _a[AvailableFilters.JobLevels] = useLookupOptions,
    _a[AvailableFilters.BusinessFunctions] = useLookupOptions,
    _a);
var PrimaryFilters = function () {
    return (_jsx(_Fragment, { children: primaryFilters.map(function (_a) {
            var filter = _a.filter, description = _a.description;
            return (_jsx(AutoCompleteFilter, { useOptions: (optionGetters[filter] || useFilterOptions)(dataFilters[filter].entity), selector: {
                    setValues: function (values) {
                        var _a;
                        updateFilters((_a = {},
                            _a[dataFilters[filter].entity] = values.map(function (_a) {
                                var value = _a.value;
                                return value;
                            }),
                            _a));
                    },
                    values: getFilters()[dataFilters[filter].entity] || [],
                }, label: dataFilters[filter].label, description: description, disabled: false }, filter));
        }) }));
};
var AJ_DESCRIPTION = "We recommend filtering to a single assessment journey, " +
    "as context differences can meaningfully affect scores and are often " +
    "misinterpreted when combined.";
var DynamicFilterAutocompletes = function (_a) {
    var filters = _a.filters;
    var ajEntity = getEntity(AvailableFilters.AssessmentJourney);
    return (_jsx(_Fragment, { children: filters
            .filter(function (_a) {
            var reportable = _a.reportable;
            return reportable;
        })
            .map(function (_a) {
            var options = _a.options, label = _a.label, entity = _a.entity, useOptions = _a.useOptions;
            return options &&
                options.length > 0 && (_jsx(AutoCompleteFilter, { useOptions: useOptions, selector: {
                    setValues: function (values) {
                        var _a;
                        updateFilters((_a = {},
                            _a[entity] = values.map(function (_a) {
                                var value = _a.value;
                                return value;
                            }),
                            _a));
                    },
                    get values() {
                        return getFilters()[entity] || [];
                    },
                }, label: label, description: entity === ajEntity ? AJ_DESCRIPTION : undefined, disabled: false }, entity));
        }) }));
};
var additionalFilters = [
    AvailableFilters.AgeRange,
    AvailableFilters.Division,
    AvailableFilters.Race,
    AvailableFilters.Gender,
    AvailableFilters.Managers,
    AvailableFilters.WillingToRelocate,
    AvailableFilters.Locations,
    AvailableFilters.LocationCountry,
    AvailableFilters.Roles,
    AvailableFilters.PeoplePartner,
    AvailableFilters.SelectedForTheRole,
    AvailableFilters.ProfileType,
    AvailableFilters.CareerInterests,
    AvailableFilters.AssessmentSource,
    AvailableFilters.DerailerRisk,
    AvailableFilters.Assessed,
    AvailableFilters.Ninebox,
    AvailableFilters.CareerTrack,
    AvailableFilters.LossImpact,
    AvailableFilters.SecurityClearance,
    AvailableFilters.Retirement,
];
var additionalFilterFields = additionalFilters.map(function (filter) { return dataFilters[filter]; });
var AdditionalFilters = function () {
    return (_jsx(_Fragment, { children: additionalFilterFields.map(function (_a) {
            var entity = _a.entity, label = _a.label;
            return (_jsx(HidingOptionlessComponent, __assign({ useOptions: useFilterOptions(entity) }, { children: _jsx(AutoCompleteFilter, { useOptions: useFilterOptions(entity), selector: {
                        setValues: function (values) {
                            var _a;
                            updateFilters((_a = {},
                                _a[entity] = values.map(function (_a) {
                                    var value = _a.value;
                                    return value;
                                }),
                                _a));
                        },
                        get values() {
                            return getFilters()[entity] || [];
                        },
                    }, label: label, description: undefined, disabled: false }, entity) }), entity));
        }) }));
};
export var useHasAllowableFilters = function () {
    var data = useReloadableReportData().data;
    var cautionMessage = useCountInfo().cautionMessage;
    return !cautionMessage || (data === null || data === void 0 ? void 0 : data.filterCheckOverride) || false;
};
var ContinueButton = function (_a) {
    var onClick = _a.onClick;
    var setData = useReloadableReportData().setData;
    var allowed = useHasAllowableFilters();
    return (_jsx("div", __assign({ className: styles.continueButtonContainer }, { children: _jsx(Button, __assign({ text: "Continue", variant: ButtonVariant.Secondary, className: styles.continueButton }, getAutomationTag(TestTag.PrimaryActionButtonNext), { onClick: function (value) {
                onClick();
                setData(function (previous) { return (__assign(__assign({}, previous), { filterCheckOverride: value })); });
            }, disabled: !allowed })) })));
};
var Navigation = function (_a) {
    var _b, _c;
    var done = _a.done, back = _a.back, forceRerender = _a.forceRerender;
    var continueAnywayHandler = useRef(function () { });
    var getFilters = useContext(FilterContext).getFilters;
    var filters = getFilters();
    useEffect(function () {
        continueAnywayHandler.current = function () { };
    }, [
        (_b = filters === null || filters === void 0 ? void 0 : filters[getEntity(AvailableFilters.JobLevels)]) === null || _b === void 0 ? void 0 : _b.length,
        (_c = filters === null || filters === void 0 ? void 0 : filters[getEntity(AvailableFilters.AssessmentJourney)]) === null || _c === void 0 ? void 0 : _c.length,
    ]);
    return (_jsxs("div", __assign({ className: styles.navigationContainer }, { children: [_jsx(CautionCard, { onCheckboxChange: continueAnywayHandler }), _jsxs("div", __assign({ className: styles.buttonContainer }, { children: [_jsxs("div", __assign({ className: styles.leftButtonContainer }, { children: [_jsxs("div", __assign({ className: styles.backButtonContainer }, { children: [_jsx(ChevronLeft, {}), _jsx(Button, __assign({ text: "Back", variant: ButtonVariant.Neutral, className: styles.backButton }, getAutomationTag(TestTag.BtnBack), { onClick: back }))] })), _jsx("div", __assign({ className: styles.continueButtonContainer }, { children: _jsx(ContinueButton, { onClick: done, continueAnywayHandler: continueAnywayHandler }) }))] })), _jsx("div", __assign({ className: styles.rightButtonContainer }, { children: _jsx(ResetButton, __assign({ label: "Reset Filters" }, getAutomationTag(TestTag.BtnResetFilters), { onClick: function () {
                                var _a;
                                setFilters(__assign(__assign({}, emptyFilters), (_a = {}, _a[getEntity(AvailableFilters.AssessmentCategory)] = (filters === null || filters === void 0 ? void 0 : filters[getEntity(AvailableFilters.AssessmentCategory)]) ||
                                    [], _a)));
                                setTimeout(forceRerender, 0);
                            } })) }))] }))] })));
};
var SpecifiedFilters = function () { return (_jsxs(_Fragment, { children: [" ", _jsx(FilterDropdown, __assign({ title: "Primary Filters", selectors: primarySelectors, expanded: true }, { children: _jsx(PrimaryFilters, {}) })), _jsx(FilterDropdown, __assign({ title: "Additional Filters", selectors: new Set(additionalFilterFields.map(function (_a) {
                var entity = _a.entity;
                return entity;
            })), expanded: false }, { children: _jsx(AdditionalFilters, {}) }))] })); };
var DynamicFilters = function () {
    var orderedFilters = useDynamicOptions().orderedFilters;
    return (_jsx("div", __assign({ className: styles.dynamicFiltersContainer }, { children: _jsx(DynamicFilterAutocompletes, { filters: orderedFilters }) })));
};
export var FilterSelectPanel = function (_a) {
    var className = _a.className, done = _a.done, back = _a.back;
    var _b = useState(Math.random()), rerenderKey = _b[0], setRerenderKey = _b[1];
    var forceRerender = function () { return setRerenderKey(Math.random()); };
    return (_jsxs("div", __assign({ className: clsx(styles.panelContainer, className) }, getAutomationTag(TestTag.FiltersPanel), { children: [_jsx("div", __assign({ className: styles.instructions }, { children: "Use the filters below to identify the project(s) and leader population to include in the report. By default, all leaders are selected." })), isCohort() ? _jsx(DynamicFilters, {}) : _jsx(SpecifiedFilters, {}), _jsx(Navigation, { forceRerender: forceRerender, done: done, back: back })] }), rerenderKey));
};
